// Feature Flags Service
// Provides JavaScript access to backend feature flags via embedded data

class FeatureFlagsService {
  constructor() {
    this.cache = new Map();
    this.isLoaded = false;
  }

  /**
   * Load all feature flags from embedded data
   * @returns {Object} Loaded flags data
   */
  loadFlags() {
    if (this.isLoaded) {
      return this.cache.get('all_flags');
    }

    return this._loadEmbeddedFlags();
  }

  _loadEmbeddedFlags() {
    try {
      // Get embedded feature flags data from the page
      const scriptElement = document.getElementById('feature-flags-data');
      if (!scriptElement) {
        console.warn('⚠️ Feature flags data not found in page');
        return { flags: [], context: {}, timestamp: Date.now() };
      }

      const data = JSON.parse(scriptElement.textContent);
      
      // Cache the flags
      const cacheEntry = {
        flags: data.flags || [],
        context: data.context || {},
        timestamp: Date.now(),
        serverTimestamp: data.timestamp
      };
      
      this.cache.set('all_flags', cacheEntry);
      this.isLoaded = true;
      
      console.log('🚩 Feature flags loaded from page:', data.flags?.length || 0, 'flags');
      return cacheEntry;
      
    } catch (error) {
      console.error('❌ Failed to load embedded feature flags:', error);
      // Return empty flags on error so the app doesn't break
      return { flags: [], context: {}, timestamp: Date.now() };
    }
  }

  /**
   * Check if a specific feature flag is enabled
   * @param {string} flagName - Name of the feature flag
   * @param {Object} context - Additional context (ignored - uses server-side context)
   * @param {boolean} useLiveCheck - If true, makes a live API call instead of using cached data
   * @returns {Promise<boolean>|boolean} Whether the flag is enabled (Promise only if useLiveCheck=true)
   */
  isEnabled(flagName, context = {}, useLiveCheck = false) {
    try {
      // Context is ignored since we use server-side rendered flags
      if (Object.keys(context).length > 0) {
        console.warn('⚠️ Additional context ignored - feature flags use server-side context');
      }
      
      // If live check is requested, make API call
      if (useLiveCheck) {
        return this._checkFlagLive(flagName);
      }
      
      // Otherwise use cached data
      if (!this.isLoaded) {
        this.loadFlags();
      }
      
      return this._getCachedFlag(flagName) || false;
      
    } catch (error) {
      console.error(`❌ Error checking feature flag "${flagName}":`, error);
      return false;
    }
  }

  /**
   * Check a feature flag with a live API call
   * @param {string} flagName - Name of the feature flag
   * @returns {Promise<boolean>} Whether the flag is enabled
   */
  async _checkFlagLive(flagName) {
    try {
      const response = await fetch(`/feature_flags/enabled/${encodeURIComponent(flagName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`🔄 Live check for '${flagName}':`, data.enabled);
      
      return data.enabled || false;
      
    } catch (error) {
      console.error(`❌ Error in live feature flag check for "${flagName}":`, error);
      return false;
    }
  }

  /**
   * Get all feature flags
   * @returns {Array} Array of all feature flags
   */
  getAllFlags() {
    const data = this.loadFlags();
    return data.flags || [];
  }

  /**
   * Get current user context
   * @returns {Object} User context object
   */
  getContext() {
    const data = this.loadFlags();
    return data.context || {};
  }

  /**
   * Refresh the cache (reload page to get fresh server-side flags)
   */
  refresh() {
    console.log('🔄 Refreshing feature flags requires page reload for server-side rendering');
    window.location.reload();
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.cache.clear();
    this.isLoaded = false;
  }

  // Private methods

  _getCachedFlag(flagName) {
    const cached = this.cache.get('all_flags');
    if (!cached) {
      return null;
    }

    const flag = cached.flags.find(f => f.name === flagName);
    return flag ? flag.enabled : false;
  }

  /**
   * Debug information
   */
  debug() {
    console.log('🔍 Feature Flags Service Debug:');
    console.log('  - Cache size:', this.cache.size);
    console.log('  - Is loaded:', this.isLoaded);
    console.log('  - Source: Server-side embedded data');
    
    try {
      const data = this.loadFlags();
      const flags = data.flags || [];
      const context = data.context || {};
      
      console.log('  - Total available flags:', data.total_available || flags.length);
      console.log('  - Total flags with status:', flags.length);
      console.log('  - Enabled flags:', flags.filter(f => f.enabled).map(f => f.name));
      console.log('  - Disabled flags:', flags.filter(f => !f.enabled).map(f => f.name));
      console.log('  - Context:', context);
      console.log('  - Server timestamp:', data.serverTimestamp);
      console.log('  - All flags:', flags);
      
    } catch (error) {
      console.log('  - Error getting debug info:', error.message);
    }
  }
}

// Create global instance
const featureFlagsService = new FeatureFlagsService();

// Export for module usage
export default featureFlagsService;
export { FeatureFlagsService };
